import { Badge, Dot, Footnote, SectionCard, type BadgeTone } from '../../../components/ui';
import { formatCount, formatPercentFromBps, maskPhoneDisplay } from '../../../lib/format';
import type { AvaliacaoRisco, NivelRisco } from '../estimativa';
import type { NumberHealth, SenderOption } from '../types';
import { NUMBER_QUALITY_LABEL, NUMBER_QUALITY_TONE, SENDER_KIND_SHORT } from '../vocabulario';

/**
 * Protecao do numero do comercial: o risco deste envio para o numero, fator
 * por fator, e a saude atual do numero.
 *
 * Decisoes nao obvias:
 *  1. O numero e da EMPRESA (e o WhatsApp do comercial). Bloqueado, a regua
 *     inteira para e a comissao some -- por isso o risco aparece ANTES do
 *     botao, com o que fazer em cada fator.
 *  2. Nivel geral = o pior fator. Media de riscos esconderia justamente o
 *     fator que bloqueia.
 *  3. Cor nunca sozinha: todo fator tem o nivel escrito ao lado do ponto.
 */

const TOM: Readonly<Record<NivelRisco, BadgeTone>> = { baixo: 'good', moderado: 'warning', alto: 'critical' };
const ROTULO: Readonly<Record<NivelRisco, string>> = { baixo: 'Risco baixo', moderado: 'Risco moderado', alto: 'Risco alto' };

export function ProtecaoDoNumero({
  risco,
  sender,
  health,
  travaLigada,
  className = '',
}: {
  readonly risco: AvaliacaoRisco;
  readonly sender: SenderOption | null;
  readonly health: NumberHealth | null;
  /** "Pausar sozinho se o opt-out passar de 3%" ligado neste envio. */
  readonly travaLigada: boolean;
  readonly className?: string;
}): JSX.Element {
  return (
    <SectionCard
      id="protecao-do-numero"
      icon="escudo"
      title="Proteção do número"
      description="O risco deste envio para o WhatsApp do comercial. Ajuste ritmo e janela em Configurações avançadas."
      aside={
        <Badge tone={TOM[risco.nivel]} dot>
          {ROTULO[risco.nivel]}
        </Badge>
      }
      className={className}
    >
      <div className="flex flex-col gap-4">
        {sender === null ? null : (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-sm">
            <span className="font-medium tabular-nums text-ink">{maskPhoneDisplay(sender.phoneE164)}</span>
            <Badge tone="neutral">{SENDER_KIND_SHORT[sender.kind]}</Badge>
            {!sender.connected ? (
              <>
                <Badge tone="critical" dot>
                  Desconectado
                </Badge>
                <span className="text-xs text-ink-subtle">conecte o WhatsApp do comercial no cadastro da empresa antes de disparar</span>
              </>
            ) : health === null ? (
              <span className="text-ink-subtle">sem histórico</span>
            ) : (
              <>
                <Badge tone={NUMBER_QUALITY_TONE[health.quality]} dot>
                  {NUMBER_QUALITY_LABEL[health.quality]}
                </Badge>
                <span className="text-xs text-ink-subtle">
                  {formatCount(health.sentToday)} enviadas hoje
                  {health.dailyLimit === null ? '' : ` de ${formatCount(health.dailyLimit)} permitidas pela Meta`} · bloqueios {formatPercentFromBps(health.blockRate7dBps)} em 7 dias ·
                  conectado há {formatCount(health.connectedDays)} {health.connectedDays === 1 ? 'dia' : 'dias'}
                </span>
              </>
            )}
          </div>
        )}
        <ul className="flex flex-col divide-y divide-line/70">
          {risco.fatores.map((f) => (
            <li key={f.chave} className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:gap-4">
              <p className="flex w-full shrink-0 items-center gap-2 text-sm text-ink sm:w-56">
                <Dot tone={TOM[f.nivel]} />
                <span className="font-medium">{f.rotulo}</span>
                <span className="sr-only">: {ROTULO[f.nivel]}</span>
              </p>
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-ink">
                  {f.valor} <span className="text-xs text-ink-subtle">· {ROTULO[f.nivel].toLowerCase()}</span>
                </p>
                <p className="text-xs text-ink-muted">{f.explicacao}</p>
              </div>
            </li>
          ))}
        </ul>
        {travaLigada ? null : (
          <p className="rounded-lg border border-status-warning/40 bg-status-warning/5 px-3 py-2 text-sm text-ink">
            <span className="font-medium">Trava de opt-out desligada:</span> se muita gente pedir para sair, o envio continua até o fim. Religue em Configurações avançadas.
          </p>
        )}
        {risco.recomendacao === null ? (
          <Footnote>{travaLigada ? 'Tudo dentro do seguro. A trava de opt-out continua de guarda durante o envio.' : 'Ritmo, janela e volume dentro do seguro.'}</Footnote>
        ) : (
          <p className="rounded-lg border border-status-warning/40 bg-status-warning/5 px-3 py-2 text-sm text-ink">
            <span className="font-medium">Primeiro ajuste:</span> {risco.recomendacao}
          </p>
        )}
      </div>
    </SectionCard>
  );
}
